import Button from 'components/button/Button';
import { useField } from 'formik';
import { useFocus } from 'helpers/useFocus';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { TFAType } from 'pages/register/models';
import FormTextField, { FormTextFieldProps } from './FormTextField';
import { ThemedLink } from '../themedLink/ThemedLink';

type TFAFieldProps = FormTextFieldProps & {
    toggleTFAType: (() => void) | null;
    tfaType: TFAType;
    backgroundColor?: 'white' | 'grey';
    holdFocus?: boolean;
    handleResendCode?: () => void;
    onClose?: () => void;
    showTimer?: boolean
};

export const TFAField: React.FC<TFAFieldProps> = ({
    toggleTFAType,
    tfaType,
    backgroundColor = 'grey',
    className = '',
    holdFocus,
    handleResendCode,
    onClose,
    showTimer,
    ...props
}) => {
    const { t } = useTranslation('auth');
    const [inputRef, setFocus] = useFocus<HTMLInputElement>();
    const [fieldProps, fieldMetaProps, fieldHelperProps] = useField(props.field);

    const handleBlur = (e: React.FocusEvent) => {
        fieldProps.onBlur(e);
        holdFocus && setFocus();
    };

    const [secondsLeft, setSecondsLeft] = useState(60);

    useEffect(() => {
        const timer = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onClose?.();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [onClose]);


    const [disabledResend, setDisabledResend] = useState(false);
    const sendNewCode = () => {
        if (disabledResend) return;

        handleResendCode?.();
        setSecondsLeft(60); // 🔄 reset timer

        setDisabledResend(true);
        setTimeout(() => setDisabledResend(false), 1000);
    };

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            const { value } = e.target;
            const regex = /^[0-9]*$/;
            if (regex.test(value)) {
                fieldHelperProps.setValue(value, false);
            } else {
                fieldHelperProps.setValue(fieldProps.value, true);
            }
        },
        [fieldProps.value, fieldHelperProps]
    );

    return (
        <div className="TFAInputContainer">
            {toggleTFAType && (
                <div className="SwitchTFATypeContainer">
                    <Button
                        priority="tertiary"
                        className="SwitchTFATypeButton"
                        onClick={() => {
                            setFocus();
                            toggleTFAType();
                        }}
                        type="button"
                    >
                        <span className="notranslate" translate="no">{tfaType === 'AuthenticatorApp' ? t('useSmsInstead') : t('useAppInstead')}</span>
                    </Button>
                </div>
            )}

            {tfaType === 'SMS' && !toggleTFAType && handleResendCode && (
                <ThemedLink onClick={sendNewCode} className="RegisterLink notranslate">
                    {t('resendCode')}
                </ThemedLink>
            )}

            <FormTextField
                maxLength={6}
                autoComplete={false}
                inputMode="numeric"
                {...props}
                className={`TFAHiddenField ${className}`}
                ref={inputRef}
                onBlur={handleBlur}
                onChange={onChange}
            />

            {/* ⏱ Timer UI */}
            {showTimer && <div className="TFATimer">
                Code expires in <strong>{secondsLeft}s</strong>
            </div>}

            <TFAInputMask
                value={fieldProps.value}
                className={`InputMask ${backgroundColor} ${fieldMetaProps.error && fieldMetaProps.touched ? 'ErrorInput' : ''
                    }`}
            />
        </div>
    );
};

type TFAInputMaskProps = {
    value: string;
    className?: string;
};

const TFA_INPUT_LENGTH = 6;

export const TFAInputMask: React.FC<TFAInputMaskProps> = ({ value, className }) => {
    const convertValueToMask = () =>
        Array(TFA_INPUT_LENGTH)
            .fill('')
            .map((_, index) => (
                <div key={index} className={`Point ${value[index] ? 'Filled' : ''}`}>
                    {value[index] ?? '•'}
                </div>
            ));

    return <label className={`InputMask ${className}`}>{convertValueToMask()}</label>;
};
